<template>
  <div>
    <div class="appui">
      <div class="appui-head">
        <div>
          <h1 class="appui-title">Feed</h1>
          <div class="appui-crumb">App <span class="sep">/</span> Feed</div>
        </div>
      </div>

      <!-- Vazio: nenhum canal -->
      <div v-if="!channels.length" class="appui-emptystate">
        <div class="appui-emptystate-icon"><i class="bx bx-conversation"></i></div>
        <div class="appui-emptystate-title">Nenhum canal ainda</div>
        <div class="appui-emptystate-sub">Crie canais para organizar o feed social do app — ex.: "Geral" (todos postam) ou "Avisos da organização" (mural oficial).</div>
        <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Criar o primeiro canal</button>
      </div>

      <!-- Bloco principal -->
      <section v-else class="appui-card appui-card--flush">
        <div class="appui-card-toolbar">
          <div class="appui-card-toolbar-left">
            <span class="appui-toolbar-hint"><i class="mdi mdi-drag-vertical"></i> Arraste para reordenar — a ordem vale no app.</span>
          </div>
          <div class="appui-card-toolbar-right">
            <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Novo canal</button>
          </div>
        </div>

        <table class="appui-table">
          <thead>
            <tr>
              <th style="width:56px"></th>
              <th>Canal</th>
              <th>Permissões do participante</th>
              <th style="width:110px">Status</th>
              <th style="width:120px;text-align:right">Ações</th>
            </tr>
          </thead>
          <draggable
            v-model="channels"
            :element="'tbody'"
            :options="{ animation: 180, handle: '.appui-grip', ghostClass: 'appui-drag-ghost' }"
            @end="onReorder"
          >
            <tr v-for="c in channels" :key="c.id">
              <td><span class="appui-grip" title="Arraste para reordenar">⠿</span></td>
              <td>
                <span style="font-weight:500;color:#495057">{{ c.name }}</span>
                <span v-if="!c.can_post" class="appui-badge appui-badge--blue" style="margin-left:8px" title="Só a organização publica neste canal">Mural oficial</span>
              </td>
              <td>
                <div style="display:flex;gap:6px;flex-wrap:wrap">
                  <span class="appui-perm" :class="{ on: c.can_post }"><i class="mdi mdi-pencil-outline"></i> Postar</span>
                  <span class="appui-perm" :class="{ on: c.can_like }"><i class="mdi mdi-heart-outline"></i> Curtir</span>
                  <span class="appui-perm" :class="{ on: c.can_comment }"><i class="mdi mdi-comment-outline"></i> Comentar</span>
                </div>
              </td>
              <td><span class="appui-badge" :class="c.status ? 'appui-badge--green' : 'appui-badge--muted'" role="button" @click="toggle(c)">{{ c.status ? 'Ativo' : 'Oculto' }}</span></td>
              <td style="text-align:right;white-space:nowrap">
                <button class="appui-tbtn" style="margin-right:6px" @click="openEdit(c)">Editar</button>
                <button class="appui-tbtn appui-tbtn--danger" @click="remove(c)">Excluir</button>
              </td>
            </tr>
          </draggable>
        </table>
      </section>
    </div>

    <b-modal v-model="showModal" :title="form.id ? 'Editar canal' : 'Novo canal'" hide-footer>
      <form @submit.prevent="save()">
        <div class="form-group">
          <label>Nome do canal *</label>
          <input type="text" class="form-control" v-model="form.name" placeholder="Ex.: Geral, Avisos da organização" required>
        </div>
        <label class="appui-label" style="margin-top:6px">O que o participante pode fazer neste canal</label>
        <div class="appui-perm-toggles">
          <label class="appui-perm-row">
            <span><i class="mdi mdi-pencil-outline"></i> Postar</span>
            <button type="button" class="appui-switch" :class="{ 'is-on': form.can_post }" @click="form.can_post = form.can_post ? 0 : 1"><span class="knob"></span></button>
          </label>
          <label class="appui-perm-row">
            <span><i class="mdi mdi-heart-outline"></i> Curtir</span>
            <button type="button" class="appui-switch" :class="{ 'is-on': form.can_like }" @click="form.can_like = form.can_like ? 0 : 1"><span class="knob"></span></button>
          </label>
          <label class="appui-perm-row">
            <span><i class="mdi mdi-comment-outline"></i> Comentar</span>
            <button type="button" class="appui-switch" :class="{ 'is-on': form.can_comment }" @click="form.can_comment = form.can_comment ? 0 : 1"><span class="knob"></span></button>
          </label>
        </div>
        <p v-if="!form.can_post" class="appui-sec-sub" style="margin-top:10px"><i class="mdi mdi-information-outline"></i> Sem permissão de postar, o canal funciona como mural oficial (só a organização publica).</p>
        <div class="text-right" style="margin-top:16px">
          <button type="button" class="appui-btn appui-btn--ghost" style="margin-right:8px" @click="showModal = false">Cancelar</button>
          <button type="submit" class="appui-btn appui-btn--primary" :disabled="saving">Salvar canal</button>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import services from './services'
import './app-ui.css'

const empty = () => ({ id: null, name: '', can_post: 1, can_like: 1, can_comment: 1 })

export default {
  components: { draggable },
  name: 'app-feed',
  data () {
    return {
      channels: [],
      showModal: false,
      saving: false,
      form: empty()
    }
  },
  mounted () { this.load() },
  methods: {
    load () {
      const loader = this.$loading.show()
      services.listFeed()
        .then(res => { this.channels = res.data.data })
        .catch(() => errorMessage('Falha ao carregar canais.', 3000))
        .finally(() => loader.hide())
    },
    openCreate () { this.form = empty(); this.showModal = true },
    openEdit (c) { this.form = { id: c.id, name: c.name, can_post: c.can_post, can_like: c.can_like, can_comment: c.can_comment }; this.showModal = true },
    async save () {
      this.saving = true
      try {
        const payload = { name: this.form.name, can_post: this.form.can_post, can_like: this.form.can_like, can_comment: this.form.can_comment }
        if (this.form.id) await services.updateFeed(this.form.id, payload)
        else await services.createFeed(payload)
        this.showModal = false
        successMessage('Canal salvo.', 2000)
        this.load()
      } catch (e) { errorMessage('Falha ao salvar canal.', 3000) } finally { this.saving = false }
    },
    async toggle (c) {
      try { await services.changeFeedStatus(c.id); c.status = c.status ? 0 : 1 } catch (e) { errorMessage('Falha ao alterar status.', 3000) }
    },
    async remove (c) {
      const ok = await this.$bvModal.msgBoxConfirm(`Remover o canal "${c.name}"?`, { okTitle: 'Remover', cancelTitle: 'Cancelar', okVariant: 'danger' })
      if (!ok) return
      try { await services.deleteFeed(c.id); this.load() } catch (e) { errorMessage('Falha ao remover.', 3000) }
    },
    async onReorder () {
      const itens = this.channels.map((it, idx) => ({ id: it.id, sequence: idx + 1 }))
      try { await services.reorderFeed(itens) } catch (e) { errorMessage('Falha ao reordenar.', 3000); this.load() }
    }
  }
}
</script>
